import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { VenueEntity } from '../types/venue.type';
import { ENV } from '@app/constants/env';
import { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { getToken } from '../../../src/helpers/authProvider';

const rawBaseQuery = fetchBaseQuery({
  baseUrl: `${ENV.BASE_API_URL +ENV.MASTERS_APIURL}`,
});

const authBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  incomingArgs,
  api,
  extraOptions
) => {
  let args: string | FetchArgs = incomingArgs;

  // normalize string arg to object so we can set headers
  if (typeof args === 'string') {
    args = { url: args };
  }

  try {
    const token = await getToken();
    if (token) {
      // Check if body is FormData - if so, don't create new Headers object
      // because it will lose the multipart boundary
      if ((args as FetchArgs).body instanceof FormData) {
        // For FormData, just add Authorization to existing headers or create minimal headers
        const headers = new Headers();
        headers.set('Authorization', `Bearer ${token}`);
        (args as FetchArgs).headers = headers;
        // Don't set Content-Type - browser will set it with boundary
      } else {
        const headers =
          (args as FetchArgs).headers instanceof Headers
            ? (args as FetchArgs).headers
            : new Headers((args as FetchArgs).headers);

        headers.set('Authorization', `Bearer ${token}`);
        (args as FetchArgs).headers = headers;
      }
    }

    const result = await rawBaseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 401 && token) {
      // Handle token refresh if needed
    }

    return result;
  } catch {
    return { error: { status: 500, data: 'Token retrieval failed' } } as any;
  }
};

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
  error?: string;
}

export const venueApi = createApi({
  reducerPath: 'venueApi',
  baseQuery: authBaseQuery,
  tagTypes: ['Venue'],
  endpoints: (builder) => ({
    getAllVenues: builder.query<VenueEntity[], void>({
      query: () => ({
        url: 'Venue/GetAllVenues',
        method: 'GET',
      }),
      providesTags: ['Venue']
    }),
    
    getVenueById: builder.query<VenueEntity, number>({
      query: (id) => ({
        url: `Venue/GetVenueById/${id}`,
        method: 'GET',
      }),
      providesTags: ['Venue']
    }),

    createVenue: builder.mutation<VenueEntity, Partial<VenueEntity>>({
      query: (venue) => ({
        url: 'Venue/CreateVenue',
        method: 'POST',
        body: venue,
      }),
      invalidatesTags: ['Venue']
    }),

    updateVenue: builder.mutation<VenueEntity, Partial<VenueEntity>>({
      query: (venue) => ({
        url: `Venue/UpdateVenue/${venue.mstr_Venue_ID}`,
        method: 'PUT',
        body: venue,
      }),
      invalidatesTags: ['Venue']
    })
  }),
});

export const {
  useGetAllVenuesQuery,
  useGetVenueByIdQuery,
  useCreateVenueMutation,
  useUpdateVenueMutation,
} = venueApi;
